import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";
import useShareSocialMedia from "@/components/hooks/use-share-social-media";
import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import { Divider } from "@/components/shared/divider";
import { Typography } from "@/components/shared/typography";
import { Button, DialogFooter, DialogHeader, View } from "@/components/ui";
import { IconButton } from "@/components/ui/icon-button";
import { ShareButton } from "@/components/ui/share-button";
import { app } from "@/config/env";

import { sendAnalyticData } from "../../../lib/monitoring/analytics";

interface ShareGiftCardValues {
  share(code: string): void;
  handleClose(): void;
}

const ShareGiftCardContext = createContext<ShareGiftCardValues | null>(null);

const createShareLinkMessage = (code: string) => {
  return `Hey! I got you a Luce gift card 🎁 Use code ${code} on the Luce app! Redeem here: ${app.LUCE_WEB_DOMAIN}/profile/gift-card?redeem=${code}`;
};
export function useShareGiftCard(): ShareGiftCardValues {
  const ctx = useContext(ShareGiftCardContext);
  if (!ctx) {
    throw new Error("useShareGiftCard compound parts must be rendered inside <ShareGiftCardModal>");
  }
  return ctx;
}
export function ShareGiftCardModalProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState("");

  const {
    copyLinkAction,
    sharerOtherAction,
    whatsappShareAction,
    telegramShareAction,
    smsShareAction,
    facebookMessengerShareAction,
  } = useShareSocialMedia(createShareLinkMessage(message));

  const handleCopy = () => {
    setMessage("");
    copyLinkAction();
    sendAnalyticData({
      name: "share_gift_card",
      values: {
        type: "copy",
      },
    });
  };

  const handleClose = () => {
    setMessage("");
  };

  const share = (code: string) => {
    setMessage(code);
    sendAnalyticData({ name: "initiate_share_gift_card" });
  };

  return (
    <ShareGiftCardContext.Provider value={{ share, handleClose }}>
      {children}
      {message && (
        <BottomDrawerModal open={!!message} onOpenChange={handleClose}>
          <DialogHeader className="flex flex-row items-center justify-between">
            <Typography variant="label-md">Share this code with family and friends</Typography>
            <IconButton
              variant="ghost"
              color="foreground"
              size="md"
              className="hidden p-0 md:block"
              iconName="x"
              onClick={handleClose}
            />
          </DialogHeader>

          <Divider className="my-2" />
          <View className="my-2 flex flex-1 flex-row justify-center gap-4">
            <ShareButton
              iconName="whatsappLogo"
              onClick={() => {
                whatsappShareAction();
                sendAnalyticData({
                  name: "share_gift_card",
                  values: {
                    type: "whatsapp",
                  },
                });
              }}
              label="WhatsApp"
            />
            <ShareButton
              iconName="telegramLogo"
              onClick={() => {
                telegramShareAction();
                sendAnalyticData({
                  name: "share_gift_card",
                  values: {
                    type: "telegram",
                  },
                });
              }}
              label="Telegram"
            />
            <ShareButton
              iconName="messengerLogo"
              onClick={() => {
                facebookMessengerShareAction();
                sendAnalyticData({
                  name: "share_gift_card",
                  values: {
                    type: "messenger",
                  },
                });
              }}
              label="Messenger"
            />
            <ShareButton
              iconName="chatDots"
              onClick={() => {
                smsShareAction();
                sendAnalyticData({
                  name: "share_gift_card",
                  values: {
                    type: "sms",
                  },
                });
              }}
              label="SMS"
            />
          </View>
          <Divider className="my-2" />
          <DialogFooter>
            <View className="flex flex-1 flex-row justify-center gap-2">
              <Button
                variant="secondary"
                color="CTA2"
                iconColor="CTA2"
                onClick={() => {
                  sharerOtherAction();
                  sendAnalyticData({
                    name: "share_gift_card",
                    values: {
                      type: "other",
                    },
                  });
                }}
                children="Other"
                iconName="dotsThreeOutline"
                fullWidth="full"
              />
              <Button
                variant="primary"
                color="CTA"
                onClick={handleCopy}
                children="Copy"
                iconName="copy"
                fullWidth="full"
              />
            </View>
          </DialogFooter>
        </BottomDrawerModal>
      )}
    </ShareGiftCardContext.Provider>
  );
}
